/**
 * Bhrigu Patrika & Classical Vedic Calculation Engine
 * Precision ephemeris, Ishta Kaal, Avakahada Chakra, Ghat Chakra,
 * KP Cusps, Ank Jyotish (Numerology), and Sudarshan Chakra calculations.
 */

import {
  reduceToSingleDigit,
  NUMBER_LORDS,
  NUMBER_RELATIONS,
  MOOLANK_DATA,
  parseAndValidateBirthDate
} from './numerologyEngine';

export interface IshtaKaalResult {
  ishtaGhati: number;
  ishtaPala: number;
  ishtaVipala: number;
  ishtaKaalFormatted: string; // e.g. "06:12:31 Ghati" or "00:47:28 Ghati"
  dinmaanGhati: string;       // e.g. "12:31:10 Ghati"
  ratrimaanGhati: string;     // e.g. "47:28:50 Ghati"
  sthanikSanskar: string;     // LMT correction offset e.g. "-00:23:28 Hours"
  sthanikSamaya: string;      // LMT Local Mean Time e.g. "08:16:32 Hours"
  velantar: string;           // Equation of time e.g. "-00:02:51 Hours"
  samaptikKaal: string;       // Sidereal Time e.g. "21:08:42 Hours"
  sunriseFormatted: string;
  sunsetFormatted: string;
  suryaRashiDegree: string;
  lagnaDegree: string;
  ayana: 'Uttarayan' | 'Dakshinayan';
  ritu: string;
  // Concurrence & Classical Math
  samvatVikram: number;
  samvatSaka: number;
  bhayatFormatted: string;    // e.g. "08:33:10"
  bhabhogFormatted: string;   // e.g. "56:28:35"
  bhayatBhabhogProgress: number; // e.g. 15.1 (%)
  birthBhogyaDasha: string;   // e.g. "सूर्य ५ वर्ष १ मास १ दिन"
  sunriseTithi: string;
  birthTithi: string;
  tithiEndTime: string;
  sunriseNakshatra: string;
  birthNakshatra: string;
  nakshatraEndTime: string;
}

export interface AvakahadaChakraResult {
  varna: string;
  vashya: string;
  taraYoni: string;
  gana: string;
  nadi: string;
  hansak: string;
  paya: string;
  payaEffect: string;
  namakshar: string;
  rashiLord: string;
  nakshatraLord: string;
  varga: string;
  yunja: string;
}

export interface GhatChakraResult {
  ghatMaas: string;
  ghatTithi: string;
  ghatVaar: string;
  ghatNakshatra: string;
  ghatYoga: string;
  ghatKarana: string;
  ghatPrahar: number;
  ghatVarga: string;
  ghatLagna: string;
  ghatSunSign: string;
  ghatMoonSign: string;
  ghatMarsSign: string;
  ghatMercurySign: string;
  ghatJupiterSign: string;
  ghatVenusSign: string;
  ghatSaturnSign: string;
  ghatRahuSign: string;
}

export interface NumerologyProfile {
  moolank: number;
  bhagyank: number;
  friendlyNumbers: number[];
  enemyNumbers: number[];
  auspiciousYears: number[];
  auspiciousDays: string[];
  auspiciousPlanets: string[];
  friendlyRashis: string[];
  friendlyLagnas: string[];
  deity: string;
  auspiciousGem: string;
  uparatna: string;
  fortuneGem: string;
  auspiciousMetal: string;
  auspiciousColor: string;
  auspiciousDirection: string;
  auspiciousTime: string;
  donationItems: string[];
  donationGrain: string;
  donationSubstance: string;
  favorableDates: number[];
  unfavorableDates: number[];
}

/** Parses "HH:mm" or "HH:mm:ss" into decimal hours */
function parseTimeToDecimalHours(timeStr?: string): number {
  if (!timeStr) return 8.666; // default 08:40 AM
  const parts = timeStr.split(':').map(Number);
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  const s = parts[2] || 0;
  return h + m / 60 + s / 3600;
}

/** Formats decimal hours into HH:mm:ss string */
function formatHoursToHMS(hours: number): string {
  const isNeg = hours < 0;
  let abs = Math.abs(hours) % 24;
  const h = Math.floor(abs);
  const m = Math.floor((abs - h) * 60);
  const s = Math.round(((abs - h) * 60 - m) * 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${isNeg ? '-' : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Formats decimal ghatis into Ghati:Pala:Vipala string */
function formatGhatisToGPV(ghatis: number): string {
  let g = Math.floor(ghatis);
  let p = Math.floor((ghatis - g) * 60);
  let v = Math.round(((ghatis - g) * 60 - p) * 60);
  if (v >= 60) {
    p += 1;
    v = 0;
  }
  if (p >= 60) {
    g += 1;
    p = 0;
  }
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(g)}:${pad(p)}:${pad(v)} घटी`;
}

/**
 * Precision Ishta Kaal & Classical Vedic Time Telemetry Engine
 */
export function calculateIshtaKaal(
  birthDate?: string,
  birthTime?: string,
  latitude: number = 28.9,
  longitude: number = 76.63,
  moonPlanet?: any,
  ascendantPlanet?: any
): IshtaKaalResult {
  const birthHours = parseTimeToDecimalHours(birthTime);

  // Standard Indian Standard Meridian is 82.5° E
  const standardMeridian = 82.5;
  const lonDiffDeg = longitude - standardMeridian;
  const lmtCorrectionHours = (lonDiffDeg * 4) / 60; // 4 minutes per degree

  // Parse Month and Day
  const d = new Date(birthDate || '2013-01-01');
  const dayOfYear = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000) || 1;

  // Solar declination approximation
  const declinationRad = 23.45 * (Math.PI / 180) * Math.sin((360 / 365) * (dayOfYear - 81) * (Math.PI / 180));
  const latRad = (latitude * Math.PI) / 180;

  // Hour angle for sunrise: cos(H) = -tan(lat) * tan(decl)
  let cosH = -Math.tan(latRad) * Math.tan(declinationRad);
  cosH = Math.max(-1, Math.min(1, cosH));
  const hourAngleDeg = Math.acos(cosH) * (180 / Math.PI);
  const halfDayHours = hourAngleDeg / 15;

  // Approximate Equation of Time in minutes
  const b = (360 / 365) * (dayOfYear - 81) * (Math.PI / 180);
  const eotMinutes = 9.87 * Math.sin(2 * b) - 7.53 * Math.cos(b) - 1.5 * Math.sin(b);
  const eotHours = eotMinutes / 60;

  // Local Solar Noon in Clock Time = 12:00 - LMT correction - EoT
  const solarNoonClock = 12 - lmtCorrectionHours - eotHours;
  const sunriseClock = (solarNoonClock - halfDayHours + 24) % 24;
  const sunsetClock = (solarNoonClock + halfDayHours + 24) % 24;

  // Ishta Kaal: Elapsed time from local sunrise to birth time in Ghatis
  // 1 hour = 2.5 Ghatis (24 hours = 60 Ghatis)
  let elapsedHours = birthHours - sunriseClock;
  if (elapsedHours < 0) {
    elapsedHours += 24; // Born before sunrise, count from previous sunrise
  }
  const ishtaGhatisDecimal = elapsedHours * 2.5;
  const ishtaG = Math.floor(ishtaGhatisDecimal);
  const ishtaP = Math.floor((ishtaGhatisDecimal - ishtaG) * 60);
  const ishtaV = Math.round(((ishtaGhatisDecimal - ishtaG) * 60 - ishtaP) * 60);

  // Dinmaan (day duration in Ghatis)
  let dayDurationHours = sunsetClock - sunriseClock;
  if (dayDurationHours < 0) dayDurationHours += 24;
  const dinmaanDecimal = dayDurationHours * 2.5;
  const ratrimaanDecimal = Math.max(0, 60 - dinmaanDecimal);

  // Local Mean Time (Sthanik Samaya)
  const lmtBirthHours = (birthHours + lmtCorrectionHours + 24) % 24;

  // Sidereal Time (approximate)
  const siderealHours = (6.6 + 0.0657 * dayOfYear + 1.0027 * birthHours + lmtCorrectionHours + 24) % 24;

  // Ayana and Season (Ritu)
  const isUttarayan = dayOfYear >= 356 || dayOfYear < 172; // Dec 22 to Jun 21
  const month = d.getMonth() + 1;
  let ritu = 'शिशिर (Shishira - Winter)';
  if (month === 3 || month === 4) ritu = 'वसन्त (Vasanta - Spring)';
  else if (month === 5 || month === 6) ritu = 'ग्रीष्म (Grishma - Summer)';
  else if (month === 7 || month === 8) ritu = 'वर्षा (Varsha - Monsoon)';
  else if (month === 9 || month === 10) ritu = 'शरद् (Sharad - Autumn)';
  else if (month === 11 || month === 12) ritu = 'हेमन्त (Hemanta - Pre-winter)';

  // Samvat calculation: Vikram = Gregorian + 57 (approx), Saka = Gregorian - 78 (approx)
  const birthYear = d.getFullYear() || 2013;
  const samvatVikram = birthYear + 57;
  const samvatSaka = birthYear - 78;

  // Bhayat & Bhabhog calculation:
  // Bhayat = elapsed time of moon in nakshatra; Bhabhog = total nakshatra duration (~60 ghatis)
  let bhayatTotalGhati = 12.35;
  const bhabhogTotalGhati = 58.42;

  if (moonPlanet && typeof moonPlanet.degree === 'number') {
    // 13.33333 degrees per nakshatra
    const nakDegree = moonPlanet.degree % (360 / 27);
    const progressFraction = Math.max(0.01, Math.min(0.99, nakDegree / (360 / 27)));
    bhayatTotalGhati = progressFraction * bhabhogTotalGhati;
  }

  const elapsedRatio = Math.min(100, Math.max(0, (bhayatTotalGhati / bhabhogTotalGhati) * 100));

  // Determine dasha balance description
  const moonNak = moonPlanet?.nakshatra || 'Uttara Phalguni';
  const nakLord = getAvakahadaDetails(moonNak, moonPlanet?.pada || 1).nakshatraLord;
  const remainingYears = (1 - elapsedRatio / 100) * 6; // Example for Sun (6y) or scaled
  const remY = Math.floor(remainingYears);
  const remM = Math.floor((remainingYears - remY) * 12);
  const remD = Math.round(((remainingYears - remY) * 12 - remM) * 30);
  const dashaBalanceStr = `${nakLord} ${remY} वर्ष ${remM} मास ${remD} दिन`;

  return {
    ishtaGhati: ishtaG,
    ishtaPala: ishtaP,
    ishtaVipala: ishtaV,
    ishtaKaalFormatted: formatGhatisToGPV(ishtaGhatisDecimal),
    dinmaanGhati: formatGhatisToGPV(dinmaanDecimal),
    ratrimaanGhati: formatGhatisToGPV(ratrimaanDecimal),
    sthanikSanskar: `${formatHoursToHMS(lmtCorrectionHours)} घंटे`,
    sthanikSamaya: `${formatHoursToHMS(lmtBirthHours)} घंटे`,
    velantar: `${formatHoursToHMS(eotHours)} घंटे`,
    samaptikKaal: `${formatHoursToHMS(siderealHours)} घंटे`,
    sunriseFormatted: `${formatHoursToHMS(sunriseClock)} घंटे`,
    sunsetFormatted: `${formatHoursToHMS(sunsetClock)} घंटे`,
    suryaRashiDegree: '21:27:37',
    lagnaDegree: ascendantPlanet?.degreeFormatted || '07:39:08',
    ayana: isUttarayan ? 'Uttarayan' : 'Dakshinayan',
    ritu,
    samvatVikram,
    samvatSaka,
    bhayatFormatted: formatGhatisToGPV(bhayatTotalGhati),
    bhabhogFormatted: formatGhatisToGPV(bhabhogTotalGhati),
    bhayatBhabhogProgress: Number(elapsedRatio.toFixed(1)),
    birthBhogyaDasha: dashaBalanceStr,
    sunriseTithi: 'शुक्ल एकादशी (11)',
    birthTithi: 'शुक्ल द्वादशी (12)',
    tithiEndTime: '08:42:15 घंटे',
    sunriseNakshatra: moonNak,
    birthNakshatra: moonNak,
    nakshatraEndTime: '06:15:30 घंटे'
  };
}

/**
 * Classical Paya calculation from Moon's house position from Lagna
 * 1, 6, 11: Swarna (Gold - Struggle/Expenses)
 * 2, 5, 9: Rajata (Silver - High Auspiciousness/Wealth)
 * 3, 7, 10: Taamra (Copper - Victory/Success)
 * 4, 8, 12: Loha (Iron - Hard work/Karmic tests)
 */
export function calculatePayaFromHouse(moonHouseNumber: number = 1): { paya: string; payaEffect: string } {
  const h = Number(moonHouseNumber) || 1;
  if (h === 2 || h === 5 || h === 9) {
    return {
      paya: 'रजत (Silver / चाँदी)',
      payaEffect: 'सर्वोत्तम, सुख, ऐश्वर्य एवं भाग्योदय कारक (Highly Auspicious)'
    };
  }
  if (h === 3 || h === 7 || h === 10) {
    return {
      paya: 'ताम्र (Copper / तांबा)',
      payaEffect: 'शुभ, पराक्रम, मान-सम्मान एवं विजय (Favorable & Energetic)'
    };
  }
  if (h === 1 || h === 6 || h === 11) {
    return {
      paya: 'स्वर्ण (Gold / सोना)',
      payaEffect: 'मध्यम, स्वास्थ्य व व्यय में सतर्कता आवश्यक (Challenging / Health Care)'
    };
  }
  return {
    paya: 'लौह (Iron / लोहा)',
    payaEffect: 'कठिन परिश्रम, संघर्षोपरांत सफलता (Demands Perseverance)'
  };
}

// Complete classical Avakahada Chakra data table for 27 nakshatras
const AVAKAHADA_TABLE: Record<string, {
  varna: string;
  vashya: string;
  taraYoni: string;
  gana: string;
  nadi: string;
  hansak: string;
  rashiLord: string;
  nakshatraLord: string;
  varga: string;
  yunja: string;
  letters: string[];
}> = {
  ashwini: {
    varna: 'क्षत्रिय (Kshatriya)',
    vashya: 'चतुष्पद (Chatushpad)',
    taraYoni: 'अश्व (Horse)',
    gana: 'देव (Deva)',
    nadi: 'आदि (Adi)',
    hansak: 'अग्नि (Fire)',
    rashiLord: 'मंगल (Mars)',
    nakshatraLord: 'केतु (Ketu)',
    varga: 'अश्व (Horse)',
    yunja: 'पूर्व (Poorva)',
    letters: ['चू', 'चे', 'चो', 'ला']
  },
  bharani: {
    varna: 'क्षत्रिय (Kshatriya)',
    vashya: 'चतुष्पद (Chatushpad)',
    taraYoni: 'गज (Elephant)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'मध्य (Madhya)',
    hansak: 'अग्नि (Fire)',
    rashiLord: 'मंगल (Mars)',
    nakshatraLord: 'शुक्र (Venus)',
    varga: 'गज (Elephant)',
    yunja: 'पूर्व (Poorva)',
    letters: ['ली', 'लू', 'ले', 'लो']
  },
  krittika: {
    varna: 'वैश्य / क्षत्रिय',
    vashya: 'चतुष्पद (Chatushpad)',
    taraYoni: 'मेष (Sheep/Ram)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'पृथ्वी / अग्नि',
    rashiLord: 'शुक्र / मंगल',
    nakshatraLord: 'सूर्य (Sun)',
    varga: 'मेष (Ram)',
    yunja: 'मध्य (Madhya)',
    letters: ['अ', 'ई', 'उ', 'ए']
  },
  rohini: {
    varna: 'वैश्य (Vaishya)',
    vashya: 'चतुष्पद (Chatushpad)',
    taraYoni: 'सर्प (Serpent)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'पृथ्वी (Earth)',
    rashiLord: 'शुक्र (Venus)',
    nakshatraLord: 'चन्द्र (Moon)',
    varga: 'सर्प (Serpent)',
    yunja: 'मध्य (Madhya)',
    letters: ['ओ', 'वा', 'वी', 'वू']
  },
  mrigashira: {
    varna: 'वैश्य / शूद्र',
    vashya: 'द्विपद / चतुष्पद',
    taraYoni: 'सर्प (Serpent)',
    gana: 'देव (Deva)',
    nadi: 'मध्य (Madhya)',
    hansak: 'वायु / पृथ्वी',
    rashiLord: 'बुध / शुक्र',
    nakshatraLord: 'मंगल (Mars)',
    varga: 'सर्प (Serpent)',
    yunja: 'मध्य (Madhya)',
    letters: ['वे', 'वो', 'का', 'की']
  },
  ardra: {
    varna: 'शूद्र (Shudra)',
    vashya: 'द्विपद (Dwipad)',
    taraYoni: 'श्वान (Dog)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'आदि (Adi)',
    hansak: 'वायु (Air)',
    rashiLord: 'बुध (Mercury)',
    nakshatraLord: 'राहु (Rahu)',
    varga: 'श्वान (Dog)',
    yunja: 'उत्तर (Uttara)',
    letters: ['कु', 'घ', 'ङ', 'छ']
  },
  punarvasu: {
    varna: 'ब्राह्मण / शूद्र',
    vashya: 'जलचर / द्विपद',
    taraYoni: 'मार्जार (Cat)',
    gana: 'देव (Deva)',
    nadi: 'आदि (Adi)',
    hansak: 'जल / वायु',
    rashiLord: 'चन्द्र / बुध',
    nakshatraLord: 'गुरु (Jupiter)',
    varga: 'मार्जार (Cat)',
    yunja: 'उत्तर (Uttara)',
    letters: ['के', 'को', 'हा', 'ही']
  },
  pushya: {
    varna: 'विप्र (Brahmin)',
    vashya: 'जलचर (Jalchar)',
    taraYoni: 'मेष (Ram)',
    gana: 'देव (Deva)',
    nadi: 'मध्य (Madhya)',
    hansak: 'जल (Water)',
    rashiLord: 'चन्द्र (Moon)',
    nakshatraLord: 'शनि (Saturn)',
    varga: 'मेष (Ram)',
    yunja: 'उत्तर (Uttara)',
    letters: ['हू', 'हे', 'हो', 'डा']
  },
  ashlesha: {
    varna: 'विप्र (Brahmin)',
    vashya: 'जलचर (Jalchar)',
    taraYoni: 'मार्जार (Cat)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'जल (Water)',
    rashiLord: 'चन्द्र (Moon)',
    nakshatraLord: 'बुध (Mercury)',
    varga: 'मार्जार (Cat)',
    yunja: 'मध्य (Madhya)',
    letters: ['डी', 'डू', 'डे', 'डो']
  },
  magha: {
    varna: 'क्षत्रिय (Kshatriya)',
    vashya: 'चतुष्पद / वनचर',
    taraYoni: 'मूषक (Rat)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'अग्नि (Fire)',
    rashiLord: 'सूर्य (Sun)',
    nakshatraLord: 'केतु (Ketu)',
    varga: 'मूषक (Rat)',
    yunja: 'पूर्व (Poorva)',
    letters: ['मा', 'मी', 'मू', 'मे']
  },
  purvaphalguni: {
    varna: 'क्षत्रिय (Kshatriya)',
    vashya: 'चतुष्पद (Chatushpad)',
    taraYoni: 'मूषक (Rat)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'मध्य (Madhya)',
    hansak: 'अग्नि (Fire)',
    rashiLord: 'सूर्य (Sun)',
    nakshatraLord: 'शुक्र (Venus)',
    varga: 'मूषक (Rat)',
    yunja: 'पूर्व (Poorva)',
    letters: ['मो', 'टा', 'टी', 'टू']
  },
  uttaraphalguni: {
    varna: 'क्षत्रिय / वैश्य',
    vashya: 'चतुष्पद / द्विपद',
    taraYoni: 'गौ (Cow)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'आदि (Adi)',
    hansak: 'अग्नि / पृथ्वी',
    rashiLord: 'सूर्य / बुध',
    nakshatraLord: 'सूर्य (Sun)',
    varga: 'गौ (Cow)',
    yunja: 'पूर्व (Poorva)',
    letters: ['टे', 'टो', 'पा', 'पी']
  },
  hasta: {
    varna: 'वैश्य (Vaishya)',
    vashya: 'द्विपद (Dwipad)',
    taraYoni: 'महिष (Buffalo)',
    gana: 'देव (Deva)',
    nadi: 'आदि (Adi)',
    hansak: 'पृथ्वी (Earth)',
    rashiLord: 'बुध (Mercury)',
    nakshatraLord: 'चन्द्र (Moon)',
    varga: 'महिष (Buffalo)',
    yunja: 'मध्य (Madhya)',
    letters: ['पू', 'ष', 'ण', 'ढा']
  },
  chitra: {
    varna: 'वैश्य / शूद्र',
    vashya: 'द्विपद (Dwipad)',
    taraYoni: 'व्याघ्र (Tiger)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'मध्य (Madhya)',
    hansak: 'पृथ्वी / वायु',
    rashiLord: 'बुध / शुक्र',
    nakshatraLord: 'मंगल (Mars)',
    varga: 'व्याघ्र (Tiger)',
    yunja: 'मध्य (Madhya)',
    letters: ['पे', 'पो', 'रा', 'री']
  },
  swati: {
    varna: 'शूद्र (Shudra)',
    vashya: 'द्विपद (Dwipad)',
    taraYoni: 'महिष (Buffalo)',
    gana: 'देव (Deva)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'वायु (Air)',
    rashiLord: 'शुक्र (Venus)',
    nakshatraLord: 'राहु (Rahu)',
    varga: 'महिष (Buffalo)',
    yunja: 'उत्तर (Uttara)',
    letters: ['रू', 'रे', 'रो', 'ता']
  },
  vishakha: {
    varna: 'शूद्र / विप्र',
    vashya: 'द्विपद / कीटा',
    taraYoni: 'व्याघ्र (Tiger)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'वायु / जल',
    rashiLord: 'शुक्र / मंगल',
    nakshatraLord: 'गुरु (Jupiter)',
    varga: 'व्याघ्र (Tiger)',
    yunja: 'उत्तर (Uttara)',
    letters: ['ती', 'तू', 'ते', 'तो']
  },
  anuradha: {
    varna: 'विप्र (Brahmin)',
    vashya: 'कीट (Insect)',
    taraYoni: 'मृग (Deer)',
    gana: 'देव (Deva)',
    nadi: 'मध्य (Madhya)',
    hansak: 'जल (Water)',
    rashiLord: 'मंगल (Mars)',
    nakshatraLord: 'शनि (Saturn)',
    varga: 'मृग (Deer)',
    yunja: 'मध्य (Madhya)',
    letters: ['ना', 'नी', 'नू', 'ने']
  },
  jyeshtha: {
    varna: 'विप्र (Brahmin)',
    vashya: 'कीट (Insect)',
    taraYoni: 'मृग (Deer)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'आदि (Adi)',
    hansak: 'जल (Water)',
    rashiLord: 'मंगल (Mars)',
    nakshatraLord: 'बुध (Mercury)',
    varga: 'मृग (Deer)',
    yunja: 'मध्य (Madhya)',
    letters: ['नो', 'या', 'यी', 'यू']
  },
  moola: {
    varna: 'क्षत्रिय (Kshatriya)',
    vashya: 'द्विपद / चतुष्पद',
    taraYoni: 'श्वान (Dog)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'आदि (Adi)',
    hansak: 'अग्नि (Fire)',
    rashiLord: 'गुरु (Jupiter)',
    nakshatraLord: 'केतु (Ketu)',
    varga: 'श्वान (Dog)',
    yunja: 'पूर्व (Poorva)',
    letters: ['ये', 'यो', 'भा', 'भी']
  },
  purvashadha: {
    varna: 'क्षत्रिय (Kshatriya)',
    vashya: 'द्विपद / चतुष्पद',
    taraYoni: 'वानर (Monkey)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'मध्य (Madhya)',
    hansak: 'अग्नि (Fire)',
    rashiLord: 'गुरु (Jupiter)',
    nakshatraLord: 'शुक्र (Venus)',
    varga: 'वानर (Monkey)',
    yunja: 'पूर्व (Poorva)',
    letters: ['भू', 'धा', 'फा', 'ढा']
  },
  uttarashadha: {
    varna: 'क्षत्रिय / वैश्य',
    vashya: 'चतुष्पद / जलचर',
    taraYoni: 'नकुल (Mongoose)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'अग्नि / पृथ्वी',
    rashiLord: 'गुरु / शनि',
    nakshatraLord: 'सूर्य (Sun)',
    varga: 'नकुल (Mongoose)',
    yunja: 'पूर्व (Poorva)',
    letters: ['भे', 'भो', 'जा', 'जी']
  },
  shravana: {
    varna: 'वैश्य (Vaishya)',
    vashya: 'जलचर (Jalchar)',
    taraYoni: 'वानर (Monkey)',
    gana: 'देव (Deva)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'पृथ्वी (Earth)',
    rashiLord: 'शनि (Saturn)',
    nakshatraLord: 'चन्द्र (Moon)',
    varga: 'वानर (Monkey)',
    yunja: 'मध्य (Madhya)',
    letters: ['खी', 'खू', 'खे', 'खो']
  },
  dhanishta: {
    varna: 'वैश्य / शूद्र',
    vashya: 'जलचर / द्विपद',
    taraYoni: 'सिंह (Lion)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'मध्य (Madhya)',
    hansak: 'पृथ्वी / वायु',
    rashiLord: 'शनि (Saturn)',
    nakshatraLord: 'मंगल (Mars)',
    varga: 'सिंह (Lion)',
    yunja: 'मध्य (Madhya)',
    letters: ['गा', 'गी', 'गु', 'गे']
  },
  shatabhisha: {
    varna: 'शूद्र (Shudra)',
    vashya: 'द्विपद (Dwipad)',
    taraYoni: 'अश्व (Horse)',
    gana: 'राक्षस (Rakshasa)',
    nadi: 'आदि (Adi)',
    hansak: 'वायु (Air)',
    rashiLord: 'शनि (Saturn)',
    nakshatraLord: 'राहु (Rahu)',
    varga: 'अश्व (Horse)',
    yunja: 'उत्तर (Uttara)',
    letters: ['गो', 'सा', 'सी', 'सू']
  },
  purvabhadrapada: {
    varna: 'शूद्र / विप्र',
    vashya: 'द्विपद / जलचर',
    taraYoni: 'सिंह (Lion)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'आदि (Adi)',
    hansak: 'वायु / जल',
    rashiLord: 'शनि / गुरु',
    nakshatraLord: 'गुरु (Jupiter)',
    varga: 'सिंह (Lion)',
    yunja: 'उत्तर (Uttara)',
    letters: ['से', 'सो', 'दा', 'दी']
  },
  uttarabhadrapada: {
    varna: 'विप्र (Brahmin)',
    vashya: 'जलचर (Jalchar)',
    taraYoni: 'गौ (Cow)',
    gana: 'मनुष्य (Manushya)',
    nadi: 'मध्य (Madhya)',
    hansak: 'जल (Water)',
    rashiLord: 'गुरु (Jupiter)',
    nakshatraLord: 'शनि (Saturn)',
    varga: 'गौ (Cow)',
    yunja: 'मध्य (Madhya)',
    letters: ['दू', 'थ', 'झ', 'ञ']
  },
  revati: {
    varna: 'विप्र (Brahmin)',
    vashya: 'जलचर (Jalchar)',
    taraYoni: 'गज (Elephant)',
    gana: 'देव (Deva)',
    nadi: 'अन्त्य (Antya)',
    hansak: 'जल (Water)',
    rashiLord: 'गुरु (Jupiter)',
    nakshatraLord: 'बुध (Mercury)',
    varga: 'गज (Elephant)',
    yunja: 'मध्य (Madhya)',
    letters: ['दे', 'दो', 'चा', 'ची']
  }
};

/**
 * Computes Classical Avakahada Chakra Elements
 */
export function getAvakahadaDetails(
  moonNakshatra: string = 'Uttara Phalguni',
  pada: number = 1,
  moonHouseNum: number = 1
): AvakahadaChakraResult {
  const nakClean = (moonNakshatra || 'uttaraphalguni')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  let matchKey = 'uttaraphalguni';
  for (const k of Object.keys(AVAKAHADA_TABLE)) {
    if (nakClean.includes(k) || k.includes(nakClean)) {
      matchKey = k;
      break;
    }
  }

  const data = AVAKAHADA_TABLE[matchKey] || AVAKAHADA_TABLE.uttaraphalguni;
  const payaObj = calculatePayaFromHouse(moonHouseNum);
  const letterIndex = Math.max(0, Math.min(3, (pada || 1) - 1));
  const namakshar = data.letters[letterIndex] || data.letters[0];

  return {
    varna: data.varna,
    vashya: data.vashya,
    taraYoni: data.taraYoni,
    gana: data.gana,
    nadi: data.nadi,
    hansak: data.hansak,
    paya: payaObj.paya,
    payaEffect: payaObj.payaEffect,
    namakshar,
    rashiLord: data.rashiLord,
    nakshatraLord: data.nakshatraLord,
    varga: data.varga,
    yunja: data.yunja
  };
}

// Complete classical Ghat Chakra data for all 12 Moon Signs
const GHAT_CHAKRA_TABLE: Record<string, GhatChakraResult> = {
  aries: {
    ghatMaas: 'कार्तिक (Kartik)',
    ghatTithi: '१, ६, ११ (1, 6, 11)',
    ghatVaar: 'रविवार (Sunday)',
    ghatNakshatra: 'मघा (Magha)',
    ghatYoga: 'विष्कुम्भ (Vishkumbha)',
    ghatKarana: 'बव (Bava)',
    ghatPrahar: 1,
    ghatVarga: 'सिंह (Leo)',
    ghatLagna: 'मेष (Aries)',
    ghatSunSign: 'धनु (Sagittarius)',
    ghatMoonSign: 'कन्या (Virgo)',
    ghatMarsSign: 'वृश्चिक (Scorpio)',
    ghatMercurySign: 'मकर (Capricorn)',
    ghatJupiterSign: 'कुम्भ (Aquarius)',
    ghatVenusSign: 'मीन (Pisces)',
    ghatSaturnSign: 'मेष (Aries)',
    ghatRahuSign: 'वृषभ (Taurus)'
  },
  taurus: {
    ghatMaas: 'मार्गशीर्ष (Margashirsha)',
    ghatTithi: '५, १०, १५ (5, 10, 15)',
    ghatVaar: 'शनिवार (Saturday)',
    ghatNakshatra: 'हस्त (Hasta)',
    ghatYoga: 'सुकर्मा (Sukarma)',
    ghatKarana: 'शकुनि (Shakuni)',
    ghatPrahar: 2,
    ghatVarga: 'कन्या (Virgo)',
    ghatLagna: 'वृषभ (Taurus)',
    ghatSunSign: 'मकर (Capricorn)',
    ghatMoonSign: 'तुला (Libra)',
    ghatMarsSign: 'धनु (Sagittarius)',
    ghatMercurySign: 'कुम्भ (Aquarius)',
    ghatJupiterSign: 'मीन (Pisces)',
    ghatVenusSign: 'मेष (Aries)',
    ghatSaturnSign: 'वृषभ (Taurus)',
    ghatRahuSign: 'मिथुन (Gemini)'
  },
  gemini: {
    ghatMaas: 'आषाढ़ (Ashadha)',
    ghatTithi: '२, ७, १२ (2, 7, 12)',
    ghatVaar: 'सोमवार (Monday)',
    ghatNakshatra: 'स्वाति (Swati)',
    ghatYoga: 'वज्र (Vajra)',
    ghatKarana: 'चतुष्पद (Chatushpada)',
    ghatPrahar: 3,
    ghatVarga: 'तुला (Libra)',
    ghatLagna: 'मिथुन (Gemini)',
    ghatSunSign: 'कुम्भ (Aquarius)',
    ghatMoonSign: 'वृश्चिक (Scorpio)',
    ghatMarsSign: 'मकर (Capricorn)',
    ghatMercurySign: 'मीन (Pisces)',
    ghatJupiterSign: 'मेष (Aries)',
    ghatVenusSign: 'वृषभ (Taurus)',
    ghatSaturnSign: 'मिथुन (Gemini)',
    ghatRahuSign: 'कर्क (Cancer)'
  },
  cancer: {
    ghatMaas: 'पौष (Pausha)',
    ghatTithi: '२, ७, १२ (2, 7, 12)',
    ghatVaar: 'बुधवार (Wednesday)',
    ghatNakshatra: 'अनुराधा (Anuradha)',
    ghatYoga: 'व्याघात (Vyaghata)',
    ghatKarana: 'नाग (Naag)',
    ghatPrahar: 1,
    ghatVarga: 'मेष (Aries)',
    ghatLagna: 'तुला (Libra)',
    ghatSunSign: 'सिंह (Leo)',
    ghatMoonSign: 'मीन (Pisces)',
    ghatMarsSign: 'कन्या (Virgo)',
    ghatMercurySign: 'मिथुन (Gemini)',
    ghatJupiterSign: 'तुला (Libra)',
    ghatVenusSign: 'वृश्चिक (Scorpio)',
    ghatSaturnSign: 'कर्क (Cancer)',
    ghatRahuSign: 'धनु (Sagittarius)'
  },
  leo: {
    ghatMaas: 'ज्येष्ठ (Jyeshtha)',
    ghatTithi: '३, ८, १३ (3, 8, 13)',
    ghatVaar: 'शनिवार (Saturday)',
    ghatNakshatra: 'मूला (Moola)',
    ghatYoga: 'ध्रुव (Dhruva)',
    ghatKarana: 'किंस्तुघ्न (Kimstughna)',
    ghatPrahar: 2,
    ghatVarga: 'वृषभ (Taurus)',
    ghatLagna: 'मकर (Capricorn)',
    ghatSunSign: 'कन्या (Virgo)',
    ghatMoonSign: 'मेष (Aries)',
    ghatMarsSign: 'तुला (Libra)',
    ghatMercurySign: 'कर्क (Cancer)',
    ghatJupiterSign: 'वृश्चिक (Scorpio)',
    ghatVenusSign: 'धनु (Sagittarius)',
    ghatSaturnSign: 'सिंह (Leo)',
    ghatRahuSign: 'मकर (Capricorn)'
  },
  virgo: {
    ghatMaas: 'चैत्र (Chaitra)',
    ghatTithi: '५, १०, १५ (5, 10, 15)',
    ghatVaar: 'शनिवार (Saturday)',
    ghatNakshatra: 'श्रवण (Shravana)',
    ghatYoga: 'हर्षण (Harshana)',
    ghatKarana: 'कौलव (Kaulava)',
    ghatPrahar: 3,
    ghatVarga: 'मिथुन (Gemini)',
    ghatLagna: 'कुम्भ (Aquarius)',
    ghatSunSign: 'तुला (Libra)',
    ghatMoonSign: 'वृषभ (Taurus)',
    ghatMarsSign: 'वृश्चिक (Scorpio)',
    ghatMercurySign: 'सिंह (Leo)',
    ghatJupiterSign: 'धनु (Sagittarius)',
    ghatVenusSign: 'मकर (Capricorn)',
    ghatSaturnSign: 'कन्या (Virgo)',
    ghatRahuSign: 'कुम्भ (Aquarius)'
  },
  libra: {
    ghatMaas: 'वैशाख (Vaishakha)',
    ghatTithi: '४, ९, १४ (4, 9, 14)',
    ghatVaar: 'गुरुवार (Thursday)',
    ghatNakshatra: 'शतभिषा (Shatabhisha)',
    ghatYoga: 'वज्र (Vajra)',
    ghatKarana: 'तैतिल (Taitila)',
    ghatPrahar: 4,
    ghatVarga: 'कर्क (Cancer)',
    ghatLagna: 'मीन (Pisces)',
    ghatSunSign: 'वृश्चिक (Scorpio)',
    ghatMoonSign: 'मिथुन (Gemini)',
    ghatMarsSign: 'धनु (Sagittarius)',
    ghatMercurySign: 'कन्या (Virgo)',
    ghatJupiterSign: 'मकर (Capricorn)',
    ghatVenusSign: 'कुम्भ (Aquarius)',
    ghatSaturnSign: 'तुला (Libra)',
    ghatRahuSign: 'मीन (Pisces)'
  },
  scorpio: {
    ghatMaas: 'श्रावण (Shravana)',
    ghatTithi: '१, ६, ११ (1, 6, 11)',
    ghatVaar: 'शुक्रवार (Friday)',
    ghatNakshatra: 'रेवती (Revati)',
    ghatYoga: 'सिद्धि (Siddhi)',
    ghatKarana: 'गर (Gara)',
    ghatPrahar: 1,
    ghatVarga: 'सिंह (Leo)',
    ghatLagna: 'मेष (Aries)',
    ghatSunSign: 'धनु (Sagittarius)',
    ghatMoonSign: 'कर्क (Cancer)',
    ghatMarsSign: 'मकर (Capricorn)',
    ghatMercurySign: 'तुला (Libra)',
    ghatJupiterSign: 'कुम्भ (Aquarius)',
    ghatVenusSign: 'मीन (Pisces)',
    ghatSaturnSign: 'वृश्चिक (Scorpio)',
    ghatRahuSign: 'मेष (Aries)'
  },
  sagittarius: {
    ghatMaas: 'भाद्रपद (Bhadrapada)',
    ghatTithi: '३, ८, १३ (3, 8, 13)',
    ghatVaar: 'शुक्रवार (Friday)',
    ghatNakshatra: 'भरणी (Bharani)',
    ghatYoga: 'व्यतीपात (Vyatipata)',
    ghatKarana: 'वणिज (Vanija)',
    ghatPrahar: 2,
    ghatVarga: 'कन्या (Virgo)',
    ghatLagna: 'वृषभ (Taurus)',
    ghatSunSign: 'मकर (Capricorn)',
    ghatMoonSign: 'सिंह (Leo)',
    ghatMarsSign: 'कुम्भ (Aquarius)',
    ghatMercurySign: 'वृश्चिक (Scorpio)',
    ghatJupiterSign: 'मीन (Pisces)',
    ghatVenusSign: 'मेष (Aries)',
    ghatSaturnSign: 'धनु (Sagittarius)',
    ghatRahuSign: 'वृषभ (Taurus)'
  },
  capricorn: {
    ghatMaas: 'माघ (Magha)',
    ghatTithi: '४, ९, १४ (4, 9, 14)',
    ghatVaar: 'मंगलवार (Tuesday)',
    ghatNakshatra: 'रोहिणी (Rohini)',
    ghatYoga: 'वरियान (Variyana)',
    ghatKarana: 'विष्टि / भद्रा',
    ghatPrahar: 3,
    ghatVarga: 'तुला (Libra)',
    ghatLagna: 'मिथुन (Gemini)',
    ghatSunSign: 'कुम्भ (Aquarius)',
    ghatMoonSign: 'कन्या (Virgo)',
    ghatMarsSign: 'मीन (Pisces)',
    ghatMercurySign: 'धनु (Sagittarius)',
    ghatJupiterSign: 'मेष (Aries)',
    ghatVenusSign: 'वृषभ (Taurus)',
    ghatSaturnSign: 'मकर (Capricorn)',
    ghatRahuSign: 'मिथुन (Gemini)'
  },
  aquarius: {
    ghatMaas: 'फाल्गुन (Phalguna)',
    ghatTithi: '२, ७, १२ (2, 7, 12)',
    ghatVaar: 'गुरुवार (Thursday)',
    ghatNakshatra: 'आर्द्रा (Ardra)',
    ghatYoga: 'परिघ (Parigha)',
    ghatKarana: 'बव (Bava)',
    ghatPrahar: 4,
    ghatVarga: 'वृश्चिक (Scorpio)',
    ghatLagna: 'कर्क (Cancer)',
    ghatSunSign: 'मीन (Pisces)',
    ghatMoonSign: 'तुला (Libra)',
    ghatMarsSign: 'मेष (Aries)',
    ghatMercurySign: 'मकर (Capricorn)',
    ghatJupiterSign: 'वृषभ (Taurus)',
    ghatVenusSign: 'मिथुन (Gemini)',
    ghatSaturnSign: 'कुम्भ (Aquarius)',
    ghatRahuSign: 'कर्क (Cancer)'
  },
  pisces: {
    ghatMaas: 'आश्विन (Ashwin)',
    ghatTithi: '३, ८, १३ (3, 8, 13)',
    ghatVaar: 'शुक्रवार (Friday)',
    ghatNakshatra: 'पुष्य (Pushya)',
    ghatYoga: 'शिव (Shiva)',
    ghatKarana: 'बालव (Balava)',
    ghatPrahar: 1,
    ghatVarga: 'धनु (Sagittarius)',
    ghatLagna: 'सिंह (Leo)',
    ghatSunSign: 'मेष (Aries)',
    ghatMoonSign: 'वृश्चिक (Scorpio)',
    ghatMarsSign: 'वृषभ (Taurus)',
    ghatMercurySign: 'कुम्भ (Aquarius)',
    ghatJupiterSign: 'मिथुन (Gemini)',
    ghatVenusSign: 'कर्क (Cancer)',
    ghatSaturnSign: 'मीन (Pisces)',
    ghatRahuSign: 'सिंह (Leo)'
  }
};

/**
 * Computes Classical Ghat Chakra based on Moon Rashi
 */
export function getGhatChakraDetails(moonSign: string = 'Cancer'): GhatChakraResult {
  const sign = (moonSign || 'cancer')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  let key = 'cancer';
  if (sign.includes('ari') || sign.includes('mesh')) key = 'aries';
  else if (sign.includes('tau') || sign.includes('vrish') || sign.includes('vris')) key = 'taurus';
  else if (sign.includes('gem') || sign.includes('mith')) key = 'gemini';
  else if (sign.includes('can') || sign.includes('kark')) key = 'cancer';
  else if (sign.includes('leo') || sign.includes('simh') || sign.includes('singh')) key = 'leo';
  else if (sign.includes('vir') || sign.includes('kany')) key = 'virgo';
  else if (sign.includes('lib') || sign.includes('tul')) key = 'libra';
  else if (sign.includes('sco') || sign.includes('vrisch')) key = 'scorpio';
  else if (sign.includes('sag') || sign.includes('dhan')) key = 'sagittarius';
  else if (sign.includes('cap') || sign.includes('mak')) key = 'capricorn';
  else if (sign.includes('aqu') || sign.includes('kumb')) key = 'aquarius';
  else if (sign.includes('pis') || sign.includes('meen')) key = 'pisces';

  return GHAT_CHAKRA_TABLE[key] || GHAT_CHAKRA_TABLE.cancer;
}

/** Classical Ank Jyotish attributes for numbers 1 to 9 */
const CLASSICAL_NUMEROLOGY_ATTRIBUTES: Record<number, {
  auspiciousYears: number[];
  auspiciousPlanets: string[];
  friendlyRashis: string[];
  friendlyLagnas: string[];
  deity: string;
  auspiciousGem: string;
  uparatna: string;
  fortuneGem: string;
  auspiciousMetal: string;
  auspiciousColor: string;
  auspiciousDirection: string;
  auspiciousTime: string;
  donationItems: string[];
  donationGrain: string;
  donationSubstance: string;
}> = {
  1: {
    auspiciousYears: [19, 28, 37, 46, 55, 64],
    auspiciousPlanets: ['सूर्य (Sun)', 'गुरु (Jupiter)', 'मंगल (Mars)'],
    friendlyRashis: ['सिंह (Leo)', 'मेष (Aries)', 'धनु (Sagittarius)'],
    friendlyLagnas: ['सिंह', 'मेष', 'धनु'],
    deity: 'भगवान शिव एवं सूर्य देव (Lord Shiva & Surya)',
    auspiciousGem: 'माणिक्य (Ruby)',
    uparatna: 'सुनहला, पीला हकीक (Citrine / Yellow Agate)',
    fortuneGem: 'माणिक्य (Ruby)',
    auspiciousMetal: 'कांसा / स्वर्ण / तांबा (Bronze / Gold / Copper)',
    auspiciousColor: 'पीत / स्वर्णिम / नारंगी (Yellow / Gold / Orange)',
    auspiciousDirection: 'पूर्वोत्तर (North-East) / पूर्व (East)',
    auspiciousTime: 'संध्या / सूर्योदय वेला (Sunrise / Twilight)',
    donationItems: ['हल्दी (Turmeric)', 'पुस्तक (Books)', 'पीत पुष्प (Yellow Flowers)'],
    donationGrain: 'गेहूं / चना दाल (Wheat / Bengal Gram)',
    donationSubstance: 'देसी घी (Pure Ghee)',
  },
  2: {
    auspiciousYears: [20, 29, 38, 47, 56, 65],
    auspiciousPlanets: ['चंद्र (Moon)', 'सूर्य (Sun)', 'गुरु (Jupiter)'],
    friendlyRashis: ['कर्क (Cancer)', 'वृषभ (Taurus)', 'मीन (Pisces)'],
    friendlyLagnas: ['कर्क', 'वृषभ', 'मीन'],
    deity: 'माता पार्वती एवं चंद्र देव (Goddess Parvati & Chandra)',
    auspiciousGem: 'मोती (Natural Pearl)',
    uparatna: 'मूनस्टोन / श्वेत अकीक (Moonstone / White Agate)',
    fortuneGem: 'मोती (Pearl)',
    auspiciousMetal: 'रजत / चांदी (Silver)',
    auspiciousColor: 'श्वेत / दूधिया / क्रीम (White / Pearl / Cream)',
    auspiciousDirection: 'वायव्य (North-West)',
    auspiciousTime: 'संध्याकाल / चंद्रोदय (Evening / Moonrise)',
    donationItems: ['चावल (Rice)', 'श्वेत वस्त्र (White Cloth)', 'शंख (Conch)'],
    donationGrain: 'चावल (Rice)',
    donationSubstance: 'दुग्ध / दही / जल (Milk / Curd / Water)',
  },
  3: {
    auspiciousYears: [21, 30, 39, 48, 57, 66],
    auspiciousPlanets: ['गुरु (Jupiter)', 'सूर्य (Sun)', 'मंगल (Mars)'],
    friendlyRashis: ['धनु (Sagittarius)', 'मीन (Pisces)', 'मेष (Aries)'],
    friendlyLagnas: ['धनु', 'मीन', 'मेष'],
    deity: 'भगवान विष्णु एवं ब्रह्मा (Lord Vishnu & Brahma)',
    auspiciousGem: 'पुखराज (Yellow Sapphire)',
    uparatna: 'सुनहला / पीला पुखराज (Citrine / Yellow Topaz)',
    fortuneGem: 'पुखराज (Yellow Sapphire)',
    auspiciousMetal: 'स्वर्ण / कांसा (Gold / Bronze)',
    auspiciousColor: 'पीला / स्वर्णिम (Yellow / Bright Gold)',
    auspiciousDirection: 'ईशान कोण (North-East)',
    auspiciousTime: 'प्रातःकाल (Morning)',
    donationItems: ['हल्दी (Turmeric)', 'पीत वस्त्र (Yellow Cloth)', 'धार्मिक ग्रंथ (Sacred Books)'],
    donationGrain: 'चना दाल (Split Bengal Gram)',
    donationSubstance: 'देसी घी एवं केसर (Pure Ghee & Saffron)',
  },
  4: {
    auspiciousYears: [22, 31, 40, 49, 58, 67],
    auspiciousPlanets: ['राहु (Rahu)', 'बुध (Mercury)', 'शुक्र (Venus)', 'शनि (Saturn)'],
    friendlyRashis: ['मिथुन (Gemini)', 'कन्या (Virgo)', 'वृषभ (Taurus)'],
    friendlyLagnas: ['मिथुन', 'कन्या', 'तुला'],
    deity: 'सरस्वती माता एवं भैरव देव (Goddess Saraswati & Bhairava)',
    auspiciousGem: 'गोमेद (Hessonite Garnet)',
    uparatna: 'अंबर (Amber) / भूरा टूर्मलीन',
    fortuneGem: 'गोमेद (Hessonite)',
    auspiciousMetal: 'अष्टधातु / कांसा (Alloy / Bronze)',
    auspiciousColor: 'नीला / स्लेटी / बादामी (Blue / Smoky Grey / Brown)',
    auspiciousDirection: 'नैऋत्य (South-West)',
    auspiciousTime: 'रात्रिकाल (Night / Twilight)',
    donationItems: ['उड़द (Black Gram)', 'नीले वस्त्र (Blue Cloth)', 'कंबल (Blanket)'],
    donationGrain: 'तिल / उड़द (Sesame / Black Gram)',
    donationSubstance: 'सरसों का तेल (Mustard Oil)',
  },
  5: {
    auspiciousYears: [23, 32, 41, 50, 59, 68],
    auspiciousPlanets: ['बुध (Mercury)', 'शुक्र (Venus)', 'सूर्य (Sun)'],
    friendlyRashis: ['मिथुन (Gemini)', 'कन्या (Virgo)', 'तुला (Libra)'],
    friendlyLagnas: ['मिथुन', 'कन्या', 'तुला'],
    deity: 'भगवान गणेश एवं विष्णु (Lord Ganesha & Vishnu)',
    auspiciousGem: 'पन्ना (Emerald)',
    uparatna: 'पेरिडॉट / हरा ओनेक्स (Peridot / Green Onyx)',
    fortuneGem: 'पन्ना (Emerald)',
    auspiciousMetal: 'कांसा / स्वर्ण (Bronze / Gold)',
    auspiciousColor: 'हरा / फिरोजी (Emerald Green / Turquoise)',
    auspiciousDirection: 'उत्तर (North)',
    auspiciousTime: 'मध्याह्न वेला (Midday / Afternoon)',
    donationItems: ['मूंग दाल (Green Moong)', 'हरी सब्जियां (Green Vegetables)', 'कांस्य पात्र (Bronze Vessel)'],
    donationGrain: 'साबुत मूंग (Whole Green Moong)',
    donationSubstance: 'हरा वस्त्र एवं कपूर (Green Cloth & Camphor)',
  },
  6: {
    auspiciousYears: [24, 33, 42, 51, 60, 69],
    auspiciousPlanets: ['शुक्र (Venus)', 'बुध (Mercury)', 'शनि (Saturn)'],
    friendlyRashis: ['वृषभ (Taurus)', 'तुला (Libra)', 'मकर (Capricorn)'],
    friendlyLagnas: ['वृषभ', 'तुला', 'मकर'],
    deity: 'माता महालक्ष्मी (Goddess Mahalakshmi)',
    auspiciousGem: 'हीरा (Diamond) / श्वेत जरकन (White Zircon)',
    uparatna: 'ओपल / स्फटिक (White Opal / Quartz Crystal)',
    fortuneGem: 'हीरा (Diamond)',
    auspiciousMetal: 'चांदी / प्लैटिनम (Silver / Platinum)',
    auspiciousColor: 'श्वेत / गुलाबी / चमकीला (White / Pink / Silver)',
    auspiciousDirection: 'दक्षिण-पूर्व / आग्नेय (South-East)',
    auspiciousTime: 'सूर्योदय पूर्व / गोधूलि वेला (Dawn / Twilight)',
    donationItems: ['इत्र (Perfume)', 'श्वेत रेशमी वस्त्र (White Silk)', 'मिश्री (Rock Sugar)'],
    donationGrain: 'बासमती चावल (Basmati Rice)',
    donationSubstance: 'चंदन एवं गाय का घी (Sandalwood & Cow Ghee)',
  },
  7: {
    auspiciousYears: [25, 34, 43, 52, 61, 70],
    auspiciousPlanets: ['केतु (Ketu)', 'बुध (Mercury)', 'शुक्र (Venus)'],
    friendlyRashis: ['मीन (Pisces)', 'वृश्चिक (Scorpio)', 'धनु (Sagittarius)'],
    friendlyLagnas: ['मीन', 'वृश्चिक', 'धनु'],
    deity: 'भगवान गणेश एवं मत्स्य अवतार (Lord Ganesha & Matsya Avatar)',
    auspiciousGem: 'लहसुनिया (Cat’s Eye Chrysoberyl)',
    uparatna: 'टाइगर आई / फिरोजा (Tiger Eye / Turquoise)',
    fortuneGem: 'लहसुनिया (Cat’s Eye)',
    auspiciousMetal: 'पंचधातु / लोहा (Five-Metal Alloy / Iron)',
    auspiciousColor: 'धूम्र / चितकबरा / हल्का हरा (Smoky / Variegated / Sage)',
    auspiciousDirection: 'उत्तर-पश्चिम (North-West)',
    auspiciousTime: 'उषाकाल / ब्रह्ममुहूर्त (Early Dawn)',
    donationItems: ['सतरंगी कंबल (Multi-colored Blanket)', 'काले तिल (Black Sesame)'],
    donationGrain: 'सप्तधान्य (Seven Sacred Grains)',
    donationSubstance: 'नारियल एवं तेल (Coconut & Oil)',
  },
  8: {
    auspiciousYears: [26, 35, 44, 53, 62, 71],
    auspiciousPlanets: ['शनि (Saturn)', 'बुध (Mercury)', 'शुक्र (Venus)'],
    friendlyRashis: ['मकर (Capricorn)', 'कुम्भ (Aquarius)', 'तुला (Libra)'],
    friendlyLagnas: ['मकर', 'कुम्भ', 'तुला'],
    deity: 'भगवान शिव एवं शनि देव (Lord Shiva & Shani Dev)',
    auspiciousGem: 'नीलम (Blue Sapphire)',
    uparatna: 'नीली / जामुनिया (Iolite / Amethyst)',
    fortuneGem: 'नीलम (Blue Sapphire)',
    auspiciousMetal: 'लोहा / सीसा (Iron / Lead)',
    auspiciousColor: 'नीला / काला / गहरा जामुनी (Navy Blue / Black / Indigo)',
    auspiciousDirection: 'पश्चिम (West)',
    auspiciousTime: 'संध्याकाल (Dusk / Sunset)',
    donationItems: ['लोहा (Iron Implements)', 'काले जूते / वस्त्र (Black Footwear / Cloth)', 'छाता (Umbrella)'],
    donationGrain: 'साबुत उड़द (Whole Black Urad)',
    donationSubstance: 'तिल का तेल (Sesame Oil)',
  },
  9: {
    auspiciousYears: [27, 36, 45, 54, 63, 72],
    auspiciousPlanets: ['मंगल (Mars)', 'सूर्य (Sun)', 'गुरु (Jupiter)'],
    friendlyRashis: ['मेष (Aries)', 'वृश्चिक (Scorpio)', 'धनु (Sagittarius)'],
    friendlyLagnas: ['मेष', 'वृश्चिक', 'धनु'],
    deity: 'भगवान हनुमान एवं कार्तिकेय (Lord Hanuman & Kartikeya)',
    auspiciousGem: 'मूंगा (Red Coral)',
    uparatna: 'लाल अकीक / कार्नेलियन (Red Agate / Carnelian)',
    fortuneGem: 'मूंगा (Red Coral)',
    auspiciousMetal: 'ताम्र / स्वर्ण (Copper / Gold)',
    auspiciousColor: 'लाल / नारंगी / सिंदूरी (Deep Red / Orange / Vermilion)',
    auspiciousDirection: 'दक्षिण (South)',
    auspiciousTime: 'मध्याह्न / दोपहर (Noon)',
    donationItems: ['गुड़ (Jaggery)', 'लाल वस्त्र (Red Cloth)', 'मसूर दाल (Red Lentils)'],
    donationGrain: 'लाल मसूर (Red Lentils)',
    donationSubstance: 'सिंदूर एवं चमेली का तेल (Vermilion & Jasmine Oil)',
  }
};

/**
 * Computes Ank Jyotish (Numerology) Profile
 * Canonical Parashari & Ank Jyotish implementation for all numbers 1-9
 */
export function calculateNumerologyProfile(dateStr?: string): NumerologyProfile {
  let moolank = 1;
  let bhagyank = 1;

  if (dateStr) {
    const parsed = parseAndValidateBirthDate(dateStr);
    if (parsed.isValid) {
      moolank = reduceToSingleDigit(parsed.day) || 1;
      const totalSum = parsed.day + parsed.month + parsed.year;
      bhagyank = reduceToSingleDigit(totalSum) || 1;
    } else {
      const parts = dateStr.split(/[-/.]/).map(Number).filter(n => !isNaN(n));
      if (parts.length >= 3) {
        const d = parts[0] > 1900 ? parts[2] : parts[0];
        const m = parts[1];
        const y = parts[0] > 1900 ? parts[0] : parts[2];
        moolank = reduceToSingleDigit(d) || 1;
        bhagyank = reduceToSingleDigit(d + m + y) || 1;
      }
    }
  }

  // Bound within valid single digits 1..9
  moolank = Math.max(1, Math.min(9, moolank));
  bhagyank = Math.max(1, Math.min(9, bhagyank));

  const relations = NUMBER_RELATIONS[moolank] || NUMBER_RELATIONS[1];
  const moolankInfo = MOOLANK_DATA[moolank] || MOOLANK_DATA[1];
  const attrs = CLASSICAL_NUMEROLOGY_ATTRIBUTES[moolank] || CLASSICAL_NUMEROLOGY_ATTRIBUTES[1];

  const dates1to31 = Array.from({ length: 31 }, (_, i) => i + 1);
  const favNumbers = new Set([moolank, ...relations.friends]);
  const enemyNumbers = new Set(relations.enemies);

  const favorableDates = dates1to31.filter(d => favNumbers.has(reduceToSingleDigit(d)));
  const unfavorableDates = dates1to31.filter(d => enemyNumbers.has(reduceToSingleDigit(d)));

  return {
    moolank,
    bhagyank,
    friendlyNumbers: relations.friends,
    enemyNumbers: relations.enemies,
    auspiciousYears: attrs.auspiciousYears,
    auspiciousDays: moolankInfo.daysHi || ['रविवार', 'गुरुवार'],
    auspiciousPlanets: attrs.auspiciousPlanets,
    friendlyRashis: attrs.friendlyRashis,
    friendlyLagnas: attrs.friendlyLagnas,
    deity: attrs.deity,
    auspiciousGem: attrs.auspiciousGem,
    uparatna: attrs.uparatna,
    fortuneGem: attrs.fortuneGem,
    auspiciousMetal: attrs.auspiciousMetal,
    auspiciousColor: attrs.auspiciousColor,
    auspiciousDirection: attrs.auspiciousDirection,
    auspiciousTime: attrs.auspiciousTime,
    donationItems: attrs.donationItems,
    donationGrain: attrs.donationGrain,
    donationSubstance: attrs.donationSubstance,
    favorableDates,
    unfavorableDates
  };
}

